import { lazy, Suspense, useEffect, useState } from 'react';
import { navigate } from './lib/navigation';

const LandingApp = lazy(() => import('./modules/landing/App'));
const AuthApp = lazy(() => import('./modules/auth/App'));
const BuyerApp = lazy(() => import('./modules/buyer/App'));
const SellerApp = lazy(() => import('./modules/seller/App'));
const AdminApp = lazy(() => import('./modules/admin/App'));
const SellerRoute = lazy(() => import('./modules/auth/components/SellerRoute'));
const LegalPage = lazy(() => import('./modules/legal/LegalPage'));

const ROUTE_TITLES = {
  landing: 'DROPVIVO | Live-commerce',
  login: 'DROPVIVO | Iniciar sesión',
  register: 'DROPVIVO | Crear tienda',
  forgot: 'DROPVIVO | Recuperar acceso',
  reset: 'DROPVIVO | Crear nueva contraseña',
  buyer: 'DROPVIVO | Tienda en vivo',
  seller: 'DROPVIVO | Panel del vendedor',
  admin: 'DROPVIVO | Super admin',
  terms: 'DROPVIVO | Términos y condiciones',
  privacy: 'DROPVIVO | Política de privacidad',
  payments: 'DROPVIVO | Aviso de pagos directos',
  notFound: 'DROPVIVO | Página no encontrada',
};

type RouteName = keyof typeof ROUTE_TITLES;

function getRoute(pathname: string): RouteName {
  if (pathname === '/') return 'landing';
  if (pathname === '/login') return 'login';
  if (pathname === '/registro') return 'register';
  if (pathname === '/recuperar-clave') return 'forgot';
  if (pathname === '/restablecer-clave') return 'reset';
  if (pathname === '/terminos') return 'terms';
  if (pathname === '/privacidad') return 'privacy';
  if (pathname === '/pagos-directos') return 'payments';
  if (pathname === '/panel' || pathname.startsWith('/panel/')) return 'seller';
  if (pathname === '/admin') return 'admin';
  if (pathname === '/tienda' || pathname.startsWith('/tienda/')) return 'buyer';
  return 'notFound';
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black text-white">
      <div className="space-y-3 text-center">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#00F2EA] border-t-transparent" />
        <p className="font-mono text-xs tracking-[0.2em] text-zinc-500">CARGANDO DROPVIVO</p>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-black px-6 text-white">
      <div className="max-w-md text-center">
        <p className="mb-3 font-mono text-sm text-[#00F2EA]">ERROR 404</p>
        <h1 className="mb-4 text-4xl font-black">Esta ruta no existe.</h1>
        <p className="mb-8 text-zinc-400">Regresa al inicio para continuar navegando en DROPVIVO.</p>
        <button type="button" onClick={() => navigate('/')} className="rounded-xl bg-white px-6 py-3 font-bold text-black">
          Volver al inicio
        </button>
      </div>
    </main>
  );
}

export default function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const route = getRoute(pathname);

  useEffect(() => {
    const handleNavigation = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  useEffect(() => {
    document.title = ROUTE_TITLES[route];
  }, [route]);

  return (
    <Suspense fallback={<LoadingScreen />}>
      {route === 'landing' && <LandingApp onLogin={() => navigate('/login')} onRegister={() => navigate('/registro')} />}
      {route === 'login' && (
        <AuthApp initialScreen="LOGIN" onOpenSellerDashboard={() => navigate('/panel')} onOpenBuyerDashboard={() => navigate('/tienda')} />
      )}
      {route === 'register' && (
        <AuthApp initialScreen="REGISTER" onOpenSellerDashboard={() => navigate('/panel')} onOpenBuyerDashboard={() => navigate('/tienda')} />
      )}
      {route === 'forgot' && (
        <AuthApp initialScreen="FORGOT_PASSWORD" onOpenSellerDashboard={() => navigate('/panel')} onOpenBuyerDashboard={() => navigate('/tienda')} />
      )}
      {route === 'reset' && (
        <AuthApp initialScreen="RESET_PASSWORD" onOpenSellerDashboard={() => navigate('/panel')} onOpenBuyerDashboard={() => navigate('/tienda')} />
      )}
      {route === 'buyer' && <BuyerApp slug={decodeURIComponent(pathname.split('/')[2] || '')} />}
      {route === 'seller' && (
        <SellerRoute>
          <SellerApp />
        </SellerRoute>
      )}
      {route === 'admin' && <AdminApp />}
      {route === 'terms' && <LegalPage kind="terms" />}
      {route === 'privacy' && <LegalPage kind="privacy" />}
      {route === 'payments' && <LegalPage kind="payments" />}
      {route === 'notFound' && <NotFound />}
    </Suspense>
  );
}

